/**
 * Change Password Form Component
 *
 * ISO 27001 A.9.4.3 - Système de gestion des mots de passe
 * Formulaire de changement de mot de passe avec validation et indicateur de force
 *
 * Utilisation:
 * <ChangePasswordForm
 *   onSuccess={() => notify.success('Mot de passe changé')}
 *   onCancel={() => setShowForm(false)}
 * />
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Lock,
  Eye,
  EyeOff,
  Shield,
  AlertCircle,
  CheckCircle,
  Loader2,
  X
} from 'lucide-react';
import { PasswordStrengthIndicator } from './PasswordStrengthIndicator';
import { authAPI } from '@/utils/api';

export interface ChangePasswordFormProps {
  /** Callback appelé après un changement réussi */
  onSuccess?: () => void;
  /** Callback pour annuler/fermer le formulaire */
  onCancel?: () => void;
  /** Email de l'utilisateur (pour éviter dans le mot de passe) */
  userEmail?: string;
  /** Nom complet de l'utilisateur (pour éviter dans le mot de passe) */
  userFullName?: string;
  /** Si l'utilisateur est admin (politique plus stricte) */
  isAdmin?: boolean;
  /** Mode compact (sans card wrapper) */
  compact?: boolean;
  /** Classe CSS additionnelle */
  className?: string;
}

export const ChangePasswordForm: React.FC<ChangePasswordFormProps> = ({
  onSuccess,
  onCancel,
  userEmail = '',
  userFullName = '',
  isAdmin = false,
  compact = false,
  className = '',
}) => {
  const { t } = useTranslation();
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const inputClasses = `w-full px-4 py-3 bg-white/40 border border-white/50 rounded-2xl
    focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white
    hover:bg-white hover:border-teal-400 hover:shadow-lg hover:shadow-teal-100/50
    transition-all duration-300 outline-none text-sm pr-12`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validations de base
    if (!oldPassword || !newPassword || !confirmPassword) {
      setError(t('auth.changePassword.allFieldsRequired'));
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t('auth.changePassword.passwordsMismatch'));
      return;
    }

    if (newPassword === oldPassword) {
      setError(t('auth.changePassword.passwordSameAsOld'));
      return;
    }

    setIsLoading(true);

    try {
      await authAPI.changePassword(oldPassword, newPassword);

      setSuccess(true);
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');

      // Appeler le callback de succès après un délai
      setTimeout(() => {
        onSuccess?.();
      }, 1500);

    } catch (err: any) {
      setError(err.message || t('common.errorOccurred'));
    } finally {
      setIsLoading(false);
    }
  };

  const formContent = (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Message d'erreur */}
      {error && (
        <div className="bg-red-50/80 border border-red-200 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Message de succès */}
      {success && (
        <div className="bg-green-50/80 border border-green-200 rounded-xl p-4 flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-green-700">
              {t('auth.changePassword.success')}
            </p>
            <p className="text-xs text-green-600 mt-1">
              {t('auth.changePassword.successSubtitle')}
            </p>
          </div>
        </div>
      )}

      {/* Ancien mot de passe */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('auth.changePassword.currentPassword')} <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <input
            type={showOldPassword ? 'text' : 'password'}
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
            className={inputClasses}
            placeholder={t('auth.changePassword.currentPasswordPlaceholder')}
            required
            disabled={isLoading || success}
          />
          <button
            type="button"
            onClick={() => setShowOldPassword(!showOldPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 transition-colors"
          >
            {showOldPassword ? (
              <EyeOff className="w-5 h-5" />
            ) : (
              <Eye className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>

      {/* Nouveau mot de passe */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('auth.changePassword.newPassword')} <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <input
            type={showNewPassword ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className={inputClasses}
            placeholder={t('auth.changePassword.minChars', { count: isAdmin ? 14 : 12 })}
            required
            disabled={isLoading || success}
          />
          <button
            type="button"
            onClick={() => setShowNewPassword(!showNewPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 transition-colors"
          >
            {showNewPassword ? (
              <EyeOff className="w-5 h-5" />
            ) : (
              <Eye className="w-5 h-5" />
            )}
          </button>
        </div>

        {/* Password Strength Indicator */}
        <PasswordStrengthIndicator
          password={newPassword}
          userInputs={[userEmail, userFullName]}
          isAdmin={isAdmin}
          showRequirements={true}
          showSuggestions={true}
          className="mt-3"
        />
      </div>

      {/* Confirmation nouveau mot de passe */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('auth.changePassword.confirmNewPassword')} <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <input
            type={showConfirmPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className={inputClasses}
            placeholder={t('auth.changePassword.confirmPlaceholder')}
            required
            disabled={isLoading || success}
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 transition-colors"
          >
            {showConfirmPassword ? (
              <EyeOff className="w-5 h-5" />
            ) : (
              <Eye className="w-5 h-5" />
            )}
          </button>
        </div>

        {/* Indicateur de correspondance */}
        {confirmPassword && (
          <div className={`mt-2 flex items-center gap-2 text-xs ${
            newPassword === confirmPassword
              ? 'text-green-600'
              : 'text-red-600'
          }`}>
            {newPassword === confirmPassword ? (
              <>
                <CheckCircle className="w-3.5 h-3.5" />
                <span>{t('auth.changePassword.passwordsMatch')}</span>
              </>
            ) : (
              <>
                <AlertCircle className="w-3.5 h-3.5" />
                <span>{t('auth.changePassword.passwordsMismatch')}</span>
              </>
            )}
          </div>
        )}
      </div>

      {/* Info sécurité */}
      <div className="bg-teal-50/50 border border-teal-100 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-teal-700">
              {t('auth.changePassword.securityTips')}
            </p>
            <ul className="text-xs text-teal-600 space-y-1">
              <li>• {t('auth.changePassword.tip1')}</li>
              <li>• {t('auth.changePassword.tip2')}</li>
              <li>• {t('auth.changePassword.tip3')}</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Boutons */}
      <div className="flex gap-3 pt-2">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            className="flex-1 px-4 py-3 text-gray-700 bg-white/50 hover:bg-white border border-gray-200 rounded-xl font-medium text-sm transition-all duration-300 disabled:opacity-50"
          >
            {t('auth.changePassword.cancel')}
          </button>
        )}
        <button
          type="submit"
          disabled={isLoading || success || !oldPassword || !newPassword || !confirmPassword || newPassword !== confirmPassword}
          className="flex-1 px-4 py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-xl font-medium text-sm transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>{t('auth.changePassword.changing')}</span>
            </>
          ) : success ? (
            <>
              <CheckCircle className="w-4 h-4" />
              <span>{t('auth.changePassword.changed')}</span>
            </>
          ) : (
            <>
              <Lock className="w-4 h-4" />
              <span>{t('auth.changePassword.submit')}</span>
            </>
          )}
        </button>
      </div>
    </form>
  );

  // Mode compact : retourne juste le formulaire
  if (compact) {
    return (
      <div className={className}>
        {formContent}
      </div>
    );
  }

  // Mode carte : enveloppe dans une card
  return (
    <div className={`bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10 border border-white/30 overflow-hidden ${className}`}>
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-6 py-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-xl">
              <Lock className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white">{t('auth.changePassword.title')}</h3>
              <p className="text-white/80 text-sm">{t('auth.changePassword.subtitle')}</p>
            </div>
          </div>
          {onCancel && (
            <button
              onClick={onCancel}
              className="p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-xl transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          )}
        </div>
      </div>

      {/* Contenu */}
      <div className="p-6">
        {formContent}
      </div>
    </div>
  );
};

export default ChangePasswordForm;
